import { DownloadSimple, ShoppingCart, Truck } from "@phosphor-icons/react"
import { createFileRoute, Navigate, Outlet, useLocation } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"

import { Badge, Button } from "~/components/design-system/components"
import { Module } from "~/components/design-system/pages"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import type { HeaderTab } from "~/components/reusable/HeaderTabs"
import { Tooltip } from "~/components/reusable/Tooltip"
import { AllocationInspectorProvider } from "~/components/prototype/allocation-planning/AllocationInspector"
import {
  AllocationPlanningProvider,
  AllocationViewProvider,
} from "~/components/prototype/allocation-planning/AllocationPlanningContext"
import CartDrawer from "~/components/prototype/allocation-planning/CartDrawer"
import { GenerateSuggestedQuantityButton } from "~/components/prototype/allocation-planning/replenishment/GenerateSuggestedQuantityButton"
import { RateOfSaleRefresh } from "~/components/prototype/allocation-planning/replenishment/RateOfSaleRefresh"
import StoreDetail from "~/components/prototype/allocation-planning/StoreDetail"
import TransfersView from "~/components/prototype/allocation-planning/TransfersView"
import {
  TabActionsSlotProvider,
  useTabActionsSlot,
} from "~/components/prototype/allocation-planning/TabActionsSlot"
import {
  useAllocationMutations,
  useAllocationStores,
  useAllocationTransfers,
  type CartItemWithLines,
} from "~/components/prototype/allocation-planning/useAllocationData"
import type { Store } from "~/components/prototype/allocation-planning/types"
import { NoAccessError } from "~/constants/errors"
import { useAllocationAccess, type AllocationAccess } from "~/hooks/useAllocationAccess"
import { useOutboundExportDownload } from "~/hooks/useOutboundExportDownload"
import { usePageTracking } from "~/hooks/usePageTracking"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { allocationRouteGuard } from "~/utils/routeGuards"

export const Route = createFileRoute("/_auth/allocation/_layout")({
  beforeLoad: allocationRouteGuard,
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Allocation")
  const access = useAllocationAccess()
  if (access.level === "none") throw new NoAccessError()
  return (
    // Keyed by the open tab's route: every allocation page keeps its own filters.
    <FilterScopeProvider>
      <AllocationViewProvider>
        {access.level === "full" ? (
          <FullAllocationLayout tabs={access.tabs} />
        ) : (
          <ProductReportLayout access={access} />
        )}
      </AllocationViewProvider>
    </FilterScopeProvider>
  )
}

/** The module as an organization with only the Products report sees it: its
 * tabs, no planning actions. Any other allocation path lands on the report. */
function ProductReportLayout({ access }: { access: AllocationAccess }) {
  const { pathname } = useLocation()
  return (
    <Module breadcrumb={<BreadcrumbWrapper />} tabs={{ items: access.tabs, path: "/allocation" }}>
      {access.canOpen(pathname) ? <Outlet /> : <Navigate to={access.landingPath} replace />}
    </Module>
  )
}

function FullAllocationLayout({ tabs }: { tabs: HeaderTab[] }) {
  const [transfersOpen, setTransfersOpen] = useState(false)
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null)
  const [cartItems, setCartItems] = useState<CartItemWithLines[]>([])
  const [isCartOpen, setIsCartOpen] = useState(false)

  const navigate = Route.useNavigate()
  const { stores } = useAllocationStores()
  const { transfers } = useAllocationTransfers()
  const mutations = useAllocationMutations()
  const exportDownload = useOutboundExportDownload()
  const tabActions = useTabActionsSlot()

  const selectedStoreIndex = stores.findIndex((s) => s.id === selectedStoreId)
  const selectedStore = stores[selectedStoreIndex] ?? null

  const handleAddToCart = useCallback((items: CartItemWithLines[]) => {
    setCartItems((prev) => {
      const updated = [...prev]
      for (const newItem of items) {
        const existing = updated.findIndex(
          (i) => i.productId === newItem.productId && i.storeId === newItem.storeId,
        )
        if (existing >= 0) {
          updated[existing] = newItem // latest size-level adjustment wins
        } else {
          updated.push(newItem)
        }
      }
      return updated
    })
  }, [])

  const openStoreSettings = useCallback(
    (storeId: string) => {
      setSelectedStoreId(null)
      void navigate({
        to: "/allocation/settings/$subTab",
        params: { subTab: "stores" },
        search: { store: storeId },
      })
    },
    [navigate],
  )

  const contextValue = useMemo(
    () => ({
      addToCart: handleAddToCart,
      openStoreDetail: (store: Store) => setSelectedStoreId(store.id),
    }),
    [handleAddToCart],
  )

  const handleStoreNavigate = useCallback(
    (direction: "prev" | "next") => {
      const newIndex =
        direction === "prev"
          ? Math.max(0, selectedStoreIndex - 1)
          : Math.min(stores.length - 1, selectedStoreIndex + 1)
      setSelectedStoreId(stores[newIndex]?.id ?? null)
    },
    [selectedStoreIndex, stores],
  )

  const handleConfirmShipment = useCallback(() => {
    if (cartItems.length === 0) return
    mutations.createTransfers.mutate(
      {
        lines: cartItems.flatMap((item) =>
          item.lines
            .filter((line) => line.quantity > 0)
            .map((line) => ({
              storeId: item.storeId,
              skuId: line.skuId,
              quantity: line.quantity,
              resultLineId: line.resultLineId,
            })),
        ),
      },
      {
        onSuccess: () => {
          setCartItems([])
          setIsCartOpen(false)
          setTransfersOpen(true)
        },
      },
    )
  }, [cartItems, mutations.createTransfers])

  const pendingTransferCount = transfers.filter((t) => t.status === "Open").length

  return (
    <AllocationInspectorProvider>
      <Module
        breadcrumb={<BreadcrumbWrapper />}
        tabs={{ items: tabs, path: "/allocation", actions: tabActions.slotElement }}
        actions={
          <div className="flex items-center gap-2">
            <RateOfSaleRefresh />
            <GenerateSuggestedQuantityButton />
            <Button variant="neutral" size="sm" onClick={() => setTransfersOpen(true)}>
              <Truck size={14} />
              Transfers
              {pendingTransferCount > 0 && (
                <Badge variant="default" size="xs" className="ml-1">
                  {pendingTransferCount}
                </Badge>
              )}
            </Button>
            <Button variant="neutral" size="sm" onClick={() => setIsCartOpen(true)}>
              <ShoppingCart size={14} />
              Cart · {cartItems.length}
            </Button>
          </div>
        }
      >
        <AllocationPlanningProvider value={contextValue}>
          <TabActionsSlotProvider value={tabActions.slot}>
            {transfersOpen ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex items-center gap-3 border-b border-base px-5 py-3">
                  <Button variant="transparent" size="sm" onClick={() => setTransfersOpen(false)}>
                    ← Back to Allocation Planning
                  </Button>
                  <h1 className="heading-sm text-fg-base">Stock Transfers</h1>
                  <Tooltip
                    title={
                      pendingTransferCount === 0
                        ? "No open stock transfer orders to export"
                        : "Build the export file for every open stock transfer order and download it"
                    }
                  >
                    <Button
                      variant="neutral"
                      size="sm"
                      className="ml-auto"
                      disabled={pendingTransferCount === 0}
                      loading={exportDownload.isPending}
                      onClick={() => exportDownload.mutate({ exportType: "sto" })}
                    >
                      <DownloadSimple size={14} />
                      Export open STOs
                    </Button>
                  </Tooltip>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-5">
                  <TransfersView transfers={transfers} />
                </div>
              </div>
            ) : (
              <Outlet />
            )}
          </TabActionsSlotProvider>

          {selectedStore && (
            <StoreDetail
              store={selectedStore}
              storeIndex={selectedStoreIndex}
              totalStores={stores.length}
              onClose={() => setSelectedStoreId(null)}
              onNavigate={handleStoreNavigate}
              onOpenSettings={(store) => openStoreSettings(store.id)}
              onAddToCart={handleAddToCart}
              cartItemCount={cartItems.length}
            />
          )}

          <CartDrawer
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            items={cartItems}
            onRemoveItem={(itemId) =>
              setCartItems((prev) => prev.filter((item) => item.id !== itemId))
            }
            onConfirmShipment={handleConfirmShipment}
            isConfirming={mutations.createTransfers.isPending}
          />
        </AllocationPlanningProvider>
      </Module>
    </AllocationInspectorProvider>
  )
}
